import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  FileSpreadsheet, Upload, CheckCircle2, AlertTriangle, Loader2, Download, ArrowLeft, ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * /leadership/template — self-service `.xlsm` timesheet template upload for
 * Senior Managers + Admins. Backend endpoint has already been widened to
 * accept both. Aircraft parsing / atomic swap logic is unchanged — this
 * page just gives Senior Managers a lightweight UI to hit that endpoint.
 */
export default function TimesheetTemplateAdmin() {
  const [settings, setSettings] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/timesheet-template");
      setSettings(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Cannot load template settings.");
    }
  };

  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.(xlsm|xlsx)$/i.test(file.name)) {
      toast.error("File must be .xlsm or .xlsx");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/timesheet-template", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data.aircraft_changes);
      toast.success(
        `Uploaded · ${data.aircraft_changes.added.length} added, ${data.aircraft_changes.removed.length} removed`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="template-admin-page">
      <Link
        to="/team"
        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-gray-500 hover:text-summit-primary mb-2"
        data-testid="template-admin-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to team
      </Link>
      <div className="flex items-center gap-3">
        <FileSpreadsheet className="w-6 h-6 text-summit-primary" />
        <h1 className="font-display text-3xl font-medium tracking-tight" data-testid="template-admin-title">
          Timesheet template
        </h1>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-xl">
        Upload the master <code className="bg-summit-glacier px-1 py-0.5 rounded font-mono text-xs">.xlsm</code> Time
        Sheet template. Aircraft in row 16 (columns D..AB) become the tail choices used across the whole app.
      </p>

      {error && (
        <div className="mt-6 border border-red-200 bg-red-50 text-red-800 rounded-md p-3 flex items-center gap-2" data-testid="template-admin-error">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      <section className="mt-8 border border-summit-hairline rounded-lg bg-white p-6" data-testid="template-current-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-emerald-600" />
          <h2 className="font-display text-lg font-medium tracking-tight">Current template</h2>
        </div>
        {settings ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <InfoField label="Filename" value={settings.filename || "— (using packaged default)"} testid="template-current-filename" />
            <InfoField
              label="Uploaded"
              value={settings.uploaded_at ? new Date(settings.uploaded_at).toLocaleString() : "—"}
              testid="template-current-uploaded"
            />
            <InfoField
              label="Uploaded by"
              value={settings.uploaded_by_name || settings.uploaded_by || "System default"}
              testid="template-current-uploader"
            />
          </div>
        ) : (
          <div className="text-xs text-gray-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        )}
      </section>

      <section className="mt-6 border border-summit-hairline rounded-lg bg-white p-6" data-testid="template-upload-card">
        <div className="flex items-center gap-2 mb-3">
          <Upload className="w-4 h-4 text-summit-primary" />
          <h2 className="font-display text-lg font-medium tracking-tight">Upload new template</h2>
        </div>
        <p className="text-xs text-gray-500 max-w-lg mb-5">
          Select an <b>.xlsm</b> (or .xlsx) file. We validate the aircraft list in row 16 columns D–AB before promoting
          it — if parsing fails, the existing template is untouched.
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="cursor-pointer">
            <input
              type="file"
              accept=".xlsm,.xlsx,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              disabled={uploading}
              data-testid="template-upload-input"
            />
            <span className="inline-flex items-center h-10 px-4 rounded-md bg-summit-primary text-white text-sm hover:bg-summit-primary-hover cursor-pointer" data-testid="template-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {uploading ? "Uploading…" : "Choose file"}
            </span>
          </label>

          <a
            href="/api/admin/timesheet-template/download"
            className="inline-flex items-center h-10 px-4 rounded-md border border-summit-hairline text-summit-primary text-sm hover:bg-summit-glacier-soft"
            data-testid="template-download-btn"
            title="Download the current active template"
          >
            <Download className="w-4 h-4 mr-2" /> Download current
          </a>
        </div>

        {result && (
          <div className="mt-5 border border-summit-hairline rounded-md bg-summit-glacier-soft p-4" data-testid="template-upload-result">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-sm font-medium">Upload complete</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Added</div>
                <div className="mt-1 tabular-nums">{result.added.length}</div>
                {result.added.length > 0 && (
                  <div className="text-[11px] text-gray-500 mt-0.5">{result.added.join(", ")}</div>
                )}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Removed</div>
                <div className="mt-1 tabular-nums">{result.removed.length}</div>
                {result.removed.length > 0 && (
                  <div className="text-[11px] text-gray-500 mt-0.5">{result.removed.join(", ")}</div>
                )}
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Kept</div>
                <div className="mt-1 tabular-nums">{result.kept.length}</div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function InfoField({ label, value, testid }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="mt-1 text-sm truncate" data-testid={testid}>{value}</div>
    </div>
  );
}
